// Phase JB.3b — usePhantomBet hook.
//
// Anon-friendly. Holds a session_id (UUID v4 in localStorage). Manages a
// draft of up to 3 legs, allows locking via INSERT into phantom_bets,
// then polls every 30s until settled.
//
// N01/N03: outcome is NEVER computed client-side. It arrives from
// jb-phantom-settle which uses _shared/settlement.ts. The hook simply
// observes the row.

import { useCallback, useEffect, useRef, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";

export const PHANTOM_SESSION_STORAGE_KEY = "strikza_phantom_session";
export const PHANTOM_DRAFT_STORAGE_KEY = "strikza_phantom_draft";
export const MAX_PHANTOM_LEGS = 3;
const POLL_MS = 30_000;

export interface PhantomLegInput {
  signal_id: string;
  market?: string;
  selection?: string;
  match_id?: string;
  odds?: number;
}

export interface PhantomLeg extends PhantomLegInput {
  leg_id: string;
}

export interface PhantomDraft {
  legs: PhantomLeg[];
}

export type PhantomOutcome = "won" | "lost" | "void" | "expired" | null;

export interface PhantomRow {
  phantom_id: string;
  legs: PhantomLeg[];
  locked_at: string;
  expires_at: string;
  settled_at: string | null;
  outcome: PhantomOutcome;
}

export interface UsePhantomBetResult {
  sessionId: string;
  draft: PhantomDraft;
  locked: PhantomRow | null;
  loading: boolean;
  error: string | null;
  addLeg: (leg: PhantomLegInput) => void;
  removeLeg: (legId: string) => void;
  clearDraft: () => void;
  lockPhantom: () => Promise<PhantomRow | null>;
}

function readSessionId(): string {
  try {
    if (typeof localStorage !== "undefined") {
      const v = localStorage.getItem(PHANTOM_SESSION_STORAGE_KEY);
      if (v) return v;
    }
  } catch {
    /* ignore */
  }
  const id = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `phantom-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(PHANTOM_SESSION_STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
  return id;
}

function readDraft(): PhantomDraft {
  try {
    if (typeof localStorage === "undefined") return { legs: [] };
    const raw = localStorage.getItem(PHANTOM_DRAFT_STORAGE_KEY);
    if (!raw) return { legs: [] };
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.legs)) return { legs: parsed.legs as PhantomLeg[] };
  } catch {
    /* ignore */
  }
  return { legs: [] };
}

function writeDraft(d: PhantomDraft): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(PHANTOM_DRAFT_STORAGE_KEY, JSON.stringify(d));
    }
  } catch {
    /* ignore */
  }
}

function newLegId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `leg-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function usePhantomBet(): UsePhantomBetResult {
  const sessionIdRef = useRef<string>(readSessionId());
  const [draft, setDraft] = useState<PhantomDraft>(() => readDraft());
  const [locked, setLocked] = useState<PhantomRow | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Sync draft to storage.
  useEffect(() => {
    writeDraft(draft);
  }, [draft]);

  // On mount: try to recover any unsettled phantom for this session.
  useEffect(() => {
    let cancelled = false;
    const sb = getBrowserClient();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    void (sb.from("phantom_bets" as never) as any)
      .select("phantom_id, legs, locked_at, expires_at, settled_at, outcome")
      .eq("session_id", sessionIdRef.current)
      .order("locked_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then((res: { data: PhantomRow | null; error: { message: string } | null }) => {
        if (cancelled) return;
        if (res.data) setLocked(res.data);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Poll every 30s while a phantom is locked but not settled.
  useEffect(() => {
    if (!locked || locked.outcome !== null) return;
    const sb = getBrowserClient();
    const tick = (): void => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      void (sb.from("phantom_bets" as never) as any)
        .select("phantom_id, legs, locked_at, expires_at, settled_at, outcome")
        .eq("phantom_id", locked.phantom_id)
        .maybeSingle()
        .then((res: { data: PhantomRow | null; error: { message: string } | null }) => {
          if (res.data) setLocked(res.data);
        });
    };
    const id = setInterval(tick, POLL_MS);
    return () => clearInterval(id);
  }, [locked]);

  const addLeg = useCallback((leg: PhantomLegInput) => {
    setDraft((d) => {
      if (d.legs.length >= MAX_PHANTOM_LEGS) return d;
      if (d.legs.some((l) => l.signal_id === leg.signal_id)) return d;
      return { legs: [...d.legs, { ...leg, leg_id: newLegId() }] };
    });
  }, []);

  const removeLeg = useCallback((legId: string) => {
    setDraft((d) => ({ legs: d.legs.filter((l) => l.leg_id !== legId) }));
  }, []);

  const clearDraft = useCallback(() => {
    setDraft({ legs: [] });
  }, []);

  const lockPhantom = useCallback(async (): Promise<PhantomRow | null> => {
    if (draft.legs.length === 0) {
      setError("Add at least one leg.");
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const sb = getBrowserClient();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error: insErr } = await (sb.from("phantom_bets" as never) as any)
        .insert({
          session_id: sessionIdRef.current,
          legs: draft.legs,
        })
        .select("phantom_id, legs, locked_at, expires_at, settled_at, outcome")
        .single();
      if (insErr) {
        setError(insErr.message);
        return null;
      }
      const row = data as PhantomRow;
      setLocked(row);
      setDraft({ legs: [] });
      return row;
    } finally {
      setLoading(false);
    }
  }, [draft]);

  return {
    sessionId: sessionIdRef.current,
    draft,
    locked,
    loading,
    error,
    addLeg,
    removeLeg,
    clearDraft,
    lockPhantom,
  };
}
